import { defineStore } from 'pinia';
import { useNuxtApp } from 'nuxt/app';
import { computed, ref } from 'vue';
import { useDeliveryStore } from './delivery';
import { useAuthenticationStore } from '~~/common/stores/authentication';
import ObjectCache from '~~/common/utils/ObjectCache.js';
import type { ResponseDataStrict, Schema } from '~~/common/types/api';
import type { AxiosError } from 'axios';

type PromotionDefaultType = Schema<'PromotionModel-default'>;
type PromotionCarouselType = PromotionDefaultType[];
type PromotionSeoTextType = Schema<'SeoTextModel'>;
type PromotionTileType = Schema<'TilePromotionModel'>;
type PromotionTileElementType = Schema<'TilePromotionElementModel'>;
type PromotionShortlistType = Schema<'ShortListModel'>;
type ProductsResponseType = ResponseDataStrict<'/api/v2/products', 'get'>;

type PromotionType = (
    | PromotionDefaultType
    | PromotionCarouselType
    | PromotionSeoTextType
    | PromotionTileType[]
    | PromotionShortlistType
    | ProductsResponseType
);

export const usePromotionStore = defineStore('promotion', () => {
  const nuxtApp = useNuxtApp();
  const deliveryStore = useDeliveryStore();
  const authenticationStore = useAuthenticationStore();

  const tiles = ref<Record<string, PromotionTileType[]>>({});
  const contentSeoText = ref<Record<string, PromotionSeoTextType>>({});
  const banners = ref<Record<string, PromotionDefaultType>>({});
  const carousels = ref<Record<string, PromotionCarouselType>>({});
  const shortlists = ref<Record<string, PromotionShortlistType>>({});

  // actions
  function setShortlist (payload: PromotionShortlistType) {
    shortlists.value[payload.id] = payload;
  }

  async function fetchCarousel ({ id }: { id: string }) {
    const { $api, $i18n } = nuxtApp;
    const postCode = deliveryStore.getPostCode;
    const cacheKey: string[] = ['carousels', id, $i18n.locale.value, postCode];
    cache.initCache(cacheKey, () => $api.$get<PromotionCarouselType>(`promotion_carousel/${id}?page=1`));
    carousels.value[id] = await cache.getCache(cacheKey)!.value as PromotionCarouselType;
  }

  async function fetchContentSeoText ({ id }: { id: string }) {
    const { $api, $logger, $i18n } = nuxtApp;
    try {
      const postCode = deliveryStore.getPostCode;
      const cacheKey: string[] = ['contentSeoText', id, $i18n.locale.value, postCode];
      cache.initCache(cacheKey, () => $api.$get<PromotionSeoTextType>(`seo_text/${id}`));
      contentSeoText.value[id] = await cache.getCache(cacheKey)!.value as PromotionSeoTextType;
    } catch (e) {
      const error = e as AxiosError<Schema<'ErrorModel'>>;
      if (error?.response?.status !== 404) {
        $logger.error(error);
      }
    }
  }

  async function fetchBanner ({ id }: { id: string }) {
    const { $api, $i18n } = nuxtApp;
    const postCode = deliveryStore.getPostCode;
    const cacheKey: string[] = ['banners', id, $i18n.locale.value, postCode];
    cache.initCache(cacheKey, () => $api.$get<PromotionDefaultType>(`promotion/banner/${id}`));
    banners.value[id] = await cache.getCache(cacheKey)!.value as PromotionDefaultType;
  }

  async function fetchTiles ({ id }: { id: string }) {
    const { $api, $i18n } = nuxtApp;
    const postCode = deliveryStore.getPostCode;
    const cacheKey: string[] = ['tiles', id, $i18n.locale.value, postCode];
    cache.initCache(cacheKey, async () => {
      const tilePromotions = await $api.$get<PromotionTileType[]>(`/promotions/tile/${id}`);
      // The backend returns the tiles in DB order, so sort them by `order` here; then
      // sort each tile's elements by their `position`. (The `position` field lives on the
      // elements, not on the TilePromotionModel — the previous top-level sort read
      // `position` off the tiles and was therefore a silent no-op.)
      return [...tilePromotions]
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map(tilePromotion => ({
          ...tilePromotion,
          elements: [...tilePromotion.elements].sort(tilePromotionSorter),
        }));
    });
    tiles.value[id] = await cache.getCache(cacheKey)!.value as PromotionTileType[];
  }

  async function fetchShortlists ({ identifier, type }: { identifier: string, type: string }) {
    await authenticationStore.reInitToken();

    const { $api, $logger, $i18n } = nuxtApp;
    try {
      const postCode = deliveryStore.getPostCode;
      const cacheKey: string[] = ['shortlists', identifier, type, $i18n.locale.value, postCode];
      identifier = encodeURIComponent(identifier);
      cache.initCache(cacheKey, () => $api.$get<PromotionShortlistType>(`/shortlist/${identifier}?type=${type}`));
      const result = (await cache.getCache(cacheKey)!.value) as PromotionShortlistType;
      setShortlist(result);
      return result?.id;
    } catch (e) {
      const error = e as AxiosError<Schema<'ErrorModel'>>;
      if (error?.response?.status !== 404) {
        $logger.error(error);
      }
    }

  }

  async function fetchRecommendedProducts (carouselId: number) {
    const { $api, $i18n } = nuxtApp;
    const postCode = deliveryStore.getPostCode;
    const cacheKey: string[] = ['recommendationCarousel', carouselId.toString(), $i18n.locale.value, postCode];
    cache.initCache(cacheKey, () => $api.$get<ProductsResponseType>(`products?page=1&itemsPerPage=15&recommendationCarouselId=${carouselId}`));
    const value = (await cache.getCache(cacheKey)!.value) as ProductsResponseType;
    return value?.results || [];
  }

  async function fetchRecentlyViewedProducts () {
    const { $api, $logger } = nuxtApp;
    try {
      return (await $api.$get<ProductsResponseType>('/me/recently_viewed?itemsPerPage=12&page=1'))?.results || [];
    } catch (e) {
      const error = e as AxiosError<Schema<'ErrorModel'>>;
      $logger.error(error);
      return [];
    }
  }

  async function fetchRelatedProducts (productId: number) {
    const { $api, $logger } = nuxtApp;
    try {
      return (await $api.$get<ProductsResponseType>(`/products/${productId}/related_products?itemsPerPage=15&page=1`))?.results || [];
    } catch (e) {
      const error = e as AxiosError<Schema<'ErrorModel'>>;
      $logger.error(error);
      return [];
    }
  }

  // getters
  const getShortlistByUrl = computed(() => (url: string) => {
    const shortListKey = Object.keys(shortlists.value).find(key => url.includes(key));
    return shortListKey ? shortlists.value[shortListKey] : null;
  });

  return {
    tiles,
    contentSeoText,
    banners,
    carousels,
    shortlists,
    setShortlist,
    fetchCarousel,
    fetchContentSeoText,
    fetchBanner,
    fetchTiles,
    fetchShortlists,
    fetchRecommendedProducts,
    fetchRecentlyViewedProducts,
    fetchRelatedProducts,
    getShortlistByUrl,
  };
});

const cache = new ObjectCache<Promise<PromotionType>>('promotion', '__');

/**
 * @param {{position: number}} a
 * @param {{position: number}} b
 */
function tilePromotionSorter (a: PromotionTileElementType, b: PromotionTileElementType) {
  const aPos = a.position || 0;
  const bPos = b.position || 0;

  if (aPos < bPos) {
    return -1;
  } else if (aPos > bPos) {
    return 1;
  } else {
    return 0;
  }
}
